import React from 'react';

function sectionClass(printMode, extra = '') {
  return `amm-num-section${extra ? ` ${extra}` : ''}${printMode ? ' amm-num-section--print' : ''}`;
}

function SectionList({ id, title, items, printMode }) {
  return (
    <section className={sectionClass(printMode)} id={printMode ? undefined : `amm-section-${id}`}>
      <h2 className="amm-num-h2 amm-num-h2--section">{title}</h2>
      <div className="amm-num-prose amm-num-prose--flow">
        {items.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
    </section>
  );
}

function SectionSummary({ id, title, data, printMode }) {
  if (!data) return null;
  return (
    <section
      className={`amm-num-section amm-num-section--summary${printMode ? ' amm-num-section--print' : ''}`}
      id={printMode ? undefined : `amm-section-${id}`}
    >
      <h2 className="amm-num-h2 amm-num-h2--summary">{title}</h2>
      <div className="amm-num-summary-board amm-num-print-block">
        <div className="amm-num-summary-topline">
          <span className="amm-num-summary-badge">{data.summaryBadge}</span>
          {data.displayName ? <span className="amm-num-summary-name">{data.displayName}</span> : null}
          {data.timingYear != null ? (
            <span className="amm-num-summary-year">Timing year {data.timingYear}</span>
          ) : null}
        </div>
        {data.birthDateLabel ? (
          <p className="amm-num-summary-dob">Birth date: {data.birthDateLabel}</p>
        ) : null}

        <div className="amm-num-summary-keynums" aria-label="Key numbers">
          {data.keyNumbers.map((kn, i) => (
            <div key={i} className="amm-num-keynum">
              <span className="amm-num-keynum-value">
                {kn.value}
                {kn.isMaster ? <span className="amm-num-keynum-master">M</span> : null}
              </span>
              <span className="amm-num-keynum-label">{kn.label}</span>
              {kn.hint ? <span className="amm-num-keynum-hint">{kn.hint}</span> : null}
            </div>
          ))}
        </div>

        {data.intro ? <p className="amm-num-summary-intro">{data.intro}</p> : null}
      </div>
    </section>
  );
}

function SectionOneSentence({ id, title, text, printMode }) {
  if (!text) return null;
  return (
    <section className={sectionClass(printMode)} id={printMode ? undefined : `amm-section-${id}`}>
      <h2 className="amm-num-h2 amm-num-h2--section">{title}</h2>
      <div className="amm-num-one-sentence amm-num-print-block">
        <p className="amm-num-prose">{text}</p>
      </div>
    </section>
  );
}

function SectionCorePattern({ id, title, blocks, printMode }) {
  if (!blocks?.length) return null;
  return (
    <section
      className={`${sectionClass(printMode)} core-pattern-section`}
      id={printMode ? undefined : `amm-section-${id}`}
    >
      <h2 className="amm-num-h2 amm-num-h2--section">{title}</h2>
      <div className="amm-num-core-pattern-grid">
        {blocks.map((b) => (
          <article key={b.key} className="amm-num-core-pattern-card amm-num-print-block">
            <h3 className="amm-num-h3">{b.title}</h3>
            <p className="amm-num-prose">{b.body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function SectionMethodology({ id, title, lines, printMode }) {
  return (
    <section
      className={`amm-num-section amm-num-section--method${printMode ? ' amm-num-section--print' : ''}`}
      id={printMode ? undefined : `amm-section-${id}`}
    >
      <h2 className="amm-num-h2 amm-num-h2--section">{title}</h2>
      <div className="amm-num-methodology">
        {lines.map((line, i) => (
          <p key={i} className="amm-num-method-line">
            {line}
          </p>
        ))}
      </div>
    </section>
  );
}

function SectionPatterns({ id, title, patterns, printMode }) {
  return (
    <section className={sectionClass(printMode)} id={printMode ? undefined : `amm-section-${id}`}>
      <h2 className="amm-num-h2 amm-num-h2--section">{title}</h2>
      <div className="amm-num-pattern-cards">
        {patterns.map((t, i) => (
          <article key={i} className="amm-num-pattern-card amm-num-print-block">
            <h3 className="amm-num-h3">{t.title}</h3>
            <p className="amm-num-pattern-label">Why it repeats</p>
            <p className="amm-num-prose">{t.whyItRepeats}</p>
            <p className="amm-num-pattern-label">How it may feel</p>
            <p className="amm-num-prose">{t.feelsLike}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function SectionTension({ id, title, items, printMode }) {
  return (
    <section className={sectionClass(printMode)} id={printMode ? undefined : `amm-section-${id}`}>
      <h2 className="amm-num-h2 amm-num-h2--section">{title}</h2>
      <div className="amm-num-tension-list">
        {items.map((t, i) => (
          <article key={t.id || i} className="amm-num-tension-card amm-num-print-block">
            <h3 className="amm-num-h3">{t.title}</h3>
            <p className="amm-num-pattern-label">The friction</p>
            <p className="amm-num-prose">{t.friction}</p>
            <p className="amm-num-pattern-label">Healthy integration</p>
            <p className="amm-num-prose">{t.integration}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function SectionCoreInterpretation({ id, title, blocks, printMode }) {
  return (
    <section className={sectionClass(printMode)} id={printMode ? undefined : `amm-section-${id}`}>
      <h2 className="amm-num-h2 amm-num-h2--section">{title}</h2>
      <div className="amm-num-core-grid">
        {blocks.map((b, i) => (
          <article key={b.key || i} className="amm-num-core-card amm-num-core-card--structured amm-num-print-block">
            <div className="amm-num-core-numrow">
              <span className="amm-num-core-value" aria-hidden>
                {b.value}
              </span>
              {b.isMaster ? <span className="amm-num-core-master">master</span> : null}
            </div>
            <h3 className="amm-num-core-title">
              {b.label} {b.value}
            </h3>
            <p className="amm-num-pattern-label">Core meaning</p>
            <p className="amm-num-core-meaning">{b.coreMeaning}</p>
            <p className="amm-num-pattern-label">How it shows up</p>
            <ul className="amm-num-core-list">
              {b.showsUp.map((line, j) => (
                <li key={j}>{line}</li>
              ))}
            </ul>
            <p className="amm-num-pattern-label">Watch for</p>
            <ul className="amm-num-core-list amm-num-core-list--watch">
              {b.watchFor.map((line, j) => (
                <li key={j}>{line}</li>
              ))}
            </ul>
            <p className="amm-num-pattern-label">Use it well</p>
            <ul className="amm-num-core-list amm-num-core-list--use">
              {b.useWell.map((line, j) => (
                <li key={j}>{line}</li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </section>
  );
}

function SectionTiming({ id, title, data, printMode }) {
  if (!data) return null;
  return (
    <section className={sectionClass(printMode)} id={printMode ? undefined : `amm-section-${id}`}>
      <h2 className="amm-num-h2 amm-num-h2--section">{title}</h2>
      <div className="amm-num-timing amm-num-print-block">
        <h3 className="amm-num-h3">{data.headline}</h3>
        <p className="amm-num-pattern-label">What this year highlights</p>
        <p className="amm-num-prose">{data.yearEmphasis}</p>
        <p className="amm-num-pattern-label">Good for</p>
        <ul className="amm-num-core-list">
          {data.goodFor.map((line, j) => (
            <li key={j}>{line}</li>
          ))}
        </ul>
        <p className="amm-num-pattern-label">What to ease up on</p>
        <ul className="amm-num-core-list amm-num-core-list--watch">
          {data.avoid.map((line, j) => (
            <li key={j}>{line}</li>
          ))}
        </ul>
        <p className="amm-num-pattern-label">How this year meets your deeper chart</p>
        <p className="amm-num-prose">{data.withChart}</p>
        {data.monthLayer ? (
          <div className="amm-num-timing-month">
            <h4 className="amm-num-h4">{data.monthLayer.title}</h4>
            <p className="amm-num-prose amm-num-prose--muted">{data.monthLayer.line}</p>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function SectionFocus({ id, title, bullets, printMode }) {
  return (
    <section className={sectionClass(printMode)} id={printMode ? undefined : `amm-section-${id}`}>
      <h2 className="amm-num-h2 amm-num-h2--section">{title}</h2>
      <div className="amm-num-focus-card amm-num-print-block">
        <ul className="amm-num-focus-list">
          {bullets.map((line, j) => (
            <li key={j}>{line}</li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function SectionLife({ id, title, areas, printMode }) {
  return (
    <section className={sectionClass(printMode)} id={printMode ? undefined : `amm-section-${id}`}>
      <h2 className="amm-num-h2 amm-num-h2--section">{title}</h2>
      <div className="amm-num-life-list">
        {areas.map((a) => (
          <div key={a.key} className="amm-num-life-block amm-num-print-block">
            <h3 className="amm-num-h3">{a.title}</h3>
            <p className="amm-num-pattern-label">Main theme</p>
            <p className="amm-num-prose">{a.theme}</p>
            <p className="amm-num-pattern-label">Strength</p>
            <p className="amm-num-prose">{a.strength}</p>
            <p className="amm-num-pattern-label">Risk / blind spot</p>
            <p className="amm-num-prose">{a.risk}</p>
            <p className="amm-num-pattern-label">Advice</p>
            <p className="amm-num-prose">{a.advice}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function SectionAdvancedCourse({ id, title, content, printMode }) {
  const intro =
    typeof content === 'object' && content && !Array.isArray(content) && content.intro != null
      ? content.intro
      : null;
  const items = Array.isArray(content) ? content : content?.items;
  if (!items?.length) return null;
  return (
    <section
      className={`amm-num-section amm-num-section--advanced advanced-topics-section${printMode ? ' amm-num-section--print' : ''}`}
      id={printMode ? undefined : `amm-section-${id}`}
    >
      <h2 className="amm-num-h2 amm-num-h2--section">{title}</h2>
      {intro ? <p className="amm-num-advanced-intro">{intro}</p> : null}
      <div className="amm-num-advanced-list">
        {items.map((item) => (
          <div key={item.id} className="amm-num-advanced-card amm-num-print-block">
            <h3 className="amm-num-h3">{item.title}</h3>
            <p className="amm-num-prose">{item.description}</p>
            <p className="amm-num-advanced-coverage">{item.coverageLine}</p>
            {item.moduleLine ? <p className="amm-num-advanced-module">{item.moduleLine}</p> : null}
            {item.lessonHref && item.linkCta ? (
              <p className="amm-num-advanced-link-row">
                <a className="amm-num-advanced-lesson-link" href={item.lessonHref}>
                  {item.linkCta}
                </a>
              </p>
            ) : item.linkStatus === 'missing' ? (
              <p className="amm-num-advanced-note">
                This topic is referenced in the course but does not yet have a dedicated lesson linked from this
                report.
              </p>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Shared report body for screen + print/PDF — single rendering path.
 */
export default function NumerologyReportBody({ sections, printMode }) {
  return (
    <>
      {sections.map((s) => {
        if (s.kind === 'summary') {
          return (
            <SectionSummary
              key={s.id}
              id={s.id}
              title={s.title}
              data={s.content}
              printMode={printMode}
            />
          );
        }
        if (s.kind === 'oneSentence') {
          return (
            <SectionOneSentence
              key={s.id}
              id={s.id}
              title={s.title}
              text={s.content}
              printMode={printMode}
            />
          );
        }
        if (s.kind === 'corePattern') {
          return (
            <SectionCorePattern
              key={s.id}
              id={s.id}
              title={s.title}
              blocks={s.content}
              printMode={printMode}
            />
          );
        }
        if (s.kind === 'methodology') {
          return (
            <SectionMethodology
              key={s.id}
              id={s.id}
              title={s.title}
              lines={s.content}
              printMode={printMode}
            />
          );
        }
        if (s.kind === 'list') {
          return <SectionList key={s.id} id={s.id} title={s.title} items={s.content} printMode={printMode} />;
        }
        if (s.kind === 'patterns') {
          return (
            <SectionPatterns key={s.id} id={s.id} title={s.title} patterns={s.content} printMode={printMode} />
          );
        }
        if (s.kind === 'tension') {
          return <SectionTension key={s.id} id={s.id} title={s.title} items={s.content} printMode={printMode} />;
        }
        if (s.kind === 'coreInterpretation') {
          return (
            <SectionCoreInterpretation
              key={s.id}
              id={s.id}
              title={s.title}
              blocks={s.content}
              printMode={printMode}
            />
          );
        }
        if (s.kind === 'timing') {
          return <SectionTiming key={s.id} id={s.id} title={s.title} data={s.content} printMode={printMode} />;
        }
        if (s.kind === 'focus') {
          return <SectionFocus key={s.id} id={s.id} title={s.title} bullets={s.content} printMode={printMode} />;
        }
        if (s.kind === 'life') {
          return <SectionLife key={s.id} id={s.id} title={s.title} areas={s.content} printMode={printMode} />;
        }
        if (s.kind === 'advancedCourse') {
          return (
            <SectionAdvancedCourse
              key={s.id}
              id={s.id}
              title={s.title}
              content={s.content}
              printMode={printMode}
            />
          );
        }
        return null;
      })}
    </>
  );
}
